import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Cog, Loader2, Calendar, Clock, ExternalLink, Copy, Check, Unlink, KanbanSquare, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { useCrmLabels, useGcalStatus, defaultReminderWhen } from "@/pages/pipeline/crmConfig";

// ============================= Renombrar etapas (solo dirección) =============================
const PIPE_FIELDS = [
  { k: "interesado", hint: "1ª fase del pipeline" },
  { k: "posible_cierre", hint: "Fase intermedia" },
  { k: "ganado", hint: "Cierre ganado" },
  { k: "perdido", hint: "Descartado" },
];
const KANBAN_FIELDS = [
  { k: "nuevo" }, { k: "calificando" }, { k: "calificado" }, { k: "cotizado" }, { k: "archivado" },
];

export function StageLabelsDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { kanban, pipeline } = useCrmLabels();
  const [k, setK] = useState(kanban);
  const [p, setP] = useState(pipeline);
  useEffect(() => { if (open) { setK(kanban); setP(pipeline); } }, [open]); // eslint-disable-line
  const mut = useMutation({
    mutationFn: async () => (await api.put("/crm-config/labels", { kanban: k, pipeline: p })).data,
    onSuccess: () => { toast.success("Nombres de etapas guardados"); qc.invalidateQueries({ queryKey: ["crm-labels"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="stage-labels-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cog className="h-4 w-4 text-primary" />Configurar etapas</DialogTitle>
          <DialogDescription className="text-xs">Personaliza los nombres visibles. La lógica interna no cambia.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><KanbanSquare className="h-3.5 w-3.5" />Fases del Pipeline</p>
            <div className="grid grid-cols-2 gap-2">
              {PIPE_FIELDS.map((f) => (
                <div key={f.k}>
                  <Label className="text-[11px] text-muted-foreground">{f.hint}</Label>
                  <Input value={p[f.k] || ""} onChange={(e) => setP((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`labels-pipeline-${f.k}`} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5" />Columnas de Cualificación</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {KANBAN_FIELDS.map((f) => (
                <div key={f.k}>
                  <Input value={k[f.k] || ""} onChange={(e) => setK((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`labels-kanban-${f.k}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="labels-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Configurar Google Calendar (solo dirección) =============================
export function GoogleConfigDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [redirect, setRedirect] = useState("");
  const [copied, setCopied] = useState(false);
  const cfgQ = useQuery({ queryKey: ["gcal-config"], queryFn: async () => (await api.get("/gcal/config")).data, enabled: open });
  useEffect(() => {
    if (cfgQ.data) { setCid(cfgQ.data.client_id || ""); setRedirect(cfgQ.data.redirect_uri || ""); setSecret(""); }
  }, [cfgQ.data]);
  const mut = useMutation({
    mutationFn: async () => (await api.post("/gcal/config", { client_id: cid, client_secret: secret, redirect_uri: redirect })).data,
    onSuccess: () => {
      toast.success("Google Calendar configurado");
      qc.invalidateQueries({ queryKey: ["gcal-status"] });
      qc.invalidateQueries({ queryKey: ["gcal-config"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const copyRedirect = () => { navigator.clipboard?.writeText(redirect); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const configured = cfgQ.data?.configured;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gcal-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />Configurar Google Calendar</DialogTitle>
          <DialogDescription className="text-xs">Credenciales OAuth de Google Cloud (solo una vez). Después cada agente conecta su cuenta con un clic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/30 p-3 text-xs">
            <p className="font-medium">URI de redirección autorizada</p>
            <p className="mt-1 text-muted-foreground">Pega esta URL en Google Cloud → Credenciales → "URIs de redirección autorizados".</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-background px-2 py-1.5" data-testid="gcal-redirect-uri">{redirect || "—"}</code>
              <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" onClick={copyRedirect} data-testid="gcal-copy-redirect">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
          <div><Label className="text-xs">Client ID</Label><Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="xxxxxx.apps.googleusercontent.com" data-testid="gcal-client-id" /></div>
          <div>
            <Label className="text-xs">Client Secret {configured && <span className="text-muted-foreground">(guardado · escribe para reemplazar)</span>}</Label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={configured ? "••••••••" : "GOCSPX-..."} data-testid="gcal-client-secret" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!cid || (!secret && !configured) || mut.isPending} data-testid="gcal-config-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Botón conectar / estado Google =============================
export function GcalConnect() {
  const qc = useQueryClient();
  const { data: st } = useGcalStatus();
  const [cfgOpen, setCfgOpen] = useState(false);
  const connect = async () => {
    try {
      const { data } = await api.get("/gcal/auth-url");
      window.location.href = data.url;
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/gcal/disconnect")).data,
    onSuccess: () => { toast.success("Google Calendar desconectado"); qc.invalidateQueries({ queryKey: ["gcal-status"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  if (!st) return null;

  if (st.connected) {
    return (
      <div className="flex items-center gap-2" data-testid="gcal-connected">
        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          <Calendar className="mr-1 h-3 w-3" />{st.google_email}
        </Badge>
        <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => disconnect.mutate()} data-testid="gcal-disconnect-btn">
          <Unlink className="mr-1 h-3.5 w-3.5" />Desconectar
        </Button>
      </div>
    );
  }
  if (st.configured) {
    return <Button size="sm" variant="outline" onClick={connect} data-testid="gcal-connect-btn"><Calendar className="mr-1.5 h-4 w-4" />Conectar Google Calendar</Button>;
  }
  if (st.can_configure) {
    return (
      <>
        <Button size="sm" variant="outline" onClick={() => setCfgOpen(true)} data-testid="gcal-configure-btn"><Cog className="mr-1.5 h-4 w-4" />Configurar Google Calendar</Button>
        <GoogleConfigDialog open={cfgOpen} onClose={() => setCfgOpen(false)} />
      </>
    );
  }
  return <span className="text-xs text-muted-foreground">Recordatorios internos</span>;
}

// ============================= Diálogo de recordatorio / próxima acción =============================
export function ReminderDialog({ open, onClose, entity }) {
  const qc = useQueryClient();
  const { data: st } = useGcalStatus();
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) { setWhen(entity?.next_action_at || defaultReminderWhen()); setNote(entity?.next_action_note || ""); }
  }, [open, entity]);
  const mut = useMutation({
    mutationFn: async () => {
      const url = entity.type === "opportunity"
        ? `/pipeline/opportunities/${entity.id}/reminder`
        : `/crm-leads/${entity.id}/reminder`;
      return (await api.post(url, { when, note, duration_min: 30 })).data;
    },
    onSuccess: (d) => {
      toast.success(d?.synced ? "Recordatorio creado y sincronizado con Google Calendar" : "Recordatorio guardado");
      qc.invalidateQueries({ queryKey: [entity.type === "opportunity" ? "pipeline-ops" : "crm-leads"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  if (!entity) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />Próxima acción · {entity.name}</DialogTitle>
          <DialogDescription className="text-xs">
            {st?.connected ? `Se creará un evento en tu Google Calendar (${st.google_email}).` : "Recordatorio interno. Conecta Google Calendar para sincronizarlo."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Fecha y hora *</Label><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="reminder-when" /></div>
          <div><Label className="text-xs">Nota / próxima acción</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Llamar para enviar propuesta de Japón…" data-testid="reminder-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!when || mut.isPending} data-testid="reminder-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Clock className="mr-1.5 h-4 w-4" />}Agendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
